import React, { useEffect, useRef, useState } from 'react';
import { useNavigation } from '../../utils/redirectUtils';
import { trackPostHogEvent } from '../../utils/posthogUtils';
import { startInterview } from '../../utils/interviewFlow';
import Pricing from '../../data/Pricing';
import CyclingText from './CyclingText';
import styles from './NewHero.module.css';
import heroImg from '../../assets/hero-image.webp';
import ssHeroImg1 from '../../assets/ss-landing-hero-img-1.webp';
import bhChapestProgramSeal from '../../assets/bh-cheapest-program-seal.webp';
import tb2VettedVendorSeal from '../../assets/tb2-vetted-vendor-seal.webp';

function OriginalHero({ safeSource, switchAndSaveSource, showBHCheapestProgramSeal=false, showTB2VettedVendorSeal=false, heroImageOverride=null, mobileWideLayout=false }) {
    const [isVisible, setIsVisible] = useState(false);
    const [prices, setPrices] = useState(null);
    const elementRef = useRef(null);
    const navigate = useNavigation();

    // Decide switchAndSave image or regular hero image based on source.
    // heroImageOverride lets a single page supply its own art without changing
    // the default for every other page that renders this hero.
    let heroImage = null;
    if (heroImageOverride) {
        heroImage = heroImageOverride;
    } else if (switchAndSaveSource === true) {
        heroImage = ssHeroImg1;
    } else if (switchAndSaveSource === false) {
        heroImage = heroImg;
    }

    const weightLossPhrases = [
        "Weight Loss",
        "Body Transformation", 
        "Metabolic Reset",
        "Lifestyle Change",
        "Wellness Revolution"
    ];
    
    const handleGetStarted = () => {
        
        // Enroll before tracking, so trackExposure has registered the variant
        // on this person and the submit event below carries it.
        const flow = startInterview();

        // Track submit event
        trackPostHogEvent('landing_page_submitted', {
            selected: 'get_started',
            to_path: '/interview',
            interview_flow: flow.id
        });

        navigate(flow.entryPath);
    }

    useEffect(() => {
        const loadPrices = async () => {
            const pricing = Pricing();
            const priceData = await pricing.getPrices();
            setPrices(priceData);
        };
        
        loadPrices();

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.5,
            }
        );

        let currentElement = elementRef.current;
        if (currentElement) {
            observer.observe(currentElement);
        }
        return () => {
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    }, []);

    return (
        <div
            className={styles.heroContainer}
            style={{ '--hero-bg-image': `url(${heroImage})` }}
        >
            <section className={`${styles.hero} ${mobileWideLayout ? styles.heroWide : ''}`}>
                <div ref={elementRef} className={styles.heroText}>
                    <div className={styles.heroTopLine + (isVisible ? ' animate__animated animate__fadeInUp' : '')}>
                        <span className={styles.heroSafe}>{safeSource ? 'Guided Medical Weight Loss' : 'Safe Effective Proven GLP-1'}</span>
                        {mobileWideLayout && !safeSource && (
                            <span className={`${styles.heroSafe} ${styles.heroSafeMobile}`}>Safe Effective Proven GLP-1 Weightloss Treatment</span>
                        )}
                    </div>
                    <div className={styles.heroWeight + (isVisible ? ' animate__animated animate__fadeInUp' : '')}>
                        <CyclingText phrases={weightLossPhrases} className={styles.cyclingWeight} />
                    </div>
                    <div className={styles.heroJourney + (isVisible ? ' animate__animated animate__fadeInUp' : '')}>
                        Your Journey Starts Here
                    </div>
                    <div className={`${styles.heroSubtext} ${mobileWideLayout ? styles.heroSubtextWide : ''}` + (isVisible ? ' animate__animated animate__fadeInUp' : '')}>
                        <div className={styles.bullet}>
                            <div className={styles.check}></div>
                            <span>One Price, All Doses</span>
                        </div>
                        <div className={styles.bullet}>
                            <div className={styles.check}></div>
                            <span>No telehealth visit required for most prescriptions</span>
                        </div>
                        <div className={styles.bullet}>
                            <div className={styles.check}></div>
                            <span>Real people, like you, are losing 15-26% of their body weight</span>
                        </div>
                    </div>
                    <div className={styles.buttonContainer}>
                        <div className={styles.heroButton} onClick={handleGetStarted}>
                            Get Started
                        </div>
                    </div>
                    {showBHCheapestProgramSeal && (
                        <img src={bhChapestProgramSeal} alt="BenefitHub's Cheapest GLP-1 Program" className={styles.lpSealMobile} />
                    )}
                    {showTB2VettedVendorSeal && (
                        <img src={tb2VettedVendorSeal} alt="TB2 Vetted Vendor" className={styles.lpSealMobile} />
                    )}
                    <div className={styles.secondaryImageContainer}>
                        <img src={heroImage} alt="Hero" className={styles.secondaryHeroImage} />
                    </div>
                </div>
                {showBHCheapestProgramSeal && (
                    <img src={bhChapestProgramSeal} alt="BenefitHub's Cheapest GLP-1 Program" className={styles.lpSeal} />
                )}
                {showTB2VettedVendorSeal && (
                    <img src={tb2VettedVendorSeal} alt="TB2 Vetted Vendor" className={styles.lpSeal} />
                )}
            </section>
        </div>
    );
}

export default OriginalHero;
